export enum BankTransactionType {
  InvoiceReceipt = 'InvoiceReceipt',
  Payment = 'Payment',
  Transfer = 'Transfer',
  Deposit = 'Deposit',
  Withdrawal = 'Withdrawal',
  BankCharge = 'BankCharge',
  Interest = 'Interest',
  OpeningBalance = 'OpeningBalance',
  Other = 'Other',
}

/**
 * A single allocation of part (or all) of a bank transaction to one
 * FinancialRecord, recorded when a transaction is matched (reconciliation
 * Req 4). Money is integer pence.
 */
export interface BankTransactionAllocation {
  financialRecordId: string;
  amount: number; // pence allocated to this item from this transaction
  itemType?: 'invoice' | 'bill' | 'service_charge';
  date?: string; // ISO date the allocation was applied
  /**
   * Unique id of the GL Settlement posting this allocation produced. Recorded
   * so an unmatch can reverse THIS exact event (and a later re-match can post a
   * fresh one) without the append-only ledger double-cancelling.
   */
  settlementId?: string;
}

export interface BankTransaction {
  id: string;
  ownerId: string;
  buildingId?: string;
  bankAccountId: string;
  bankAccountName: string; // denormalised for display
  description: string;
  date: string; // ISO date string YYYY-MM-DD
  type: BankTransactionType;
  grossValue: number; // in pence (positive = money in, negative = money out)
  reference?: string;
  nominalCode?: string;
  categoryName?: string;
  reconciled: boolean;
  financialRecordId?: string; // double-entry FK link to FinancialRecord

  // FreeAgent cross-reference fields (preserved on import from
  // Bank Account Transactions CSV — see /.specify/features/import/spec.md).
  paidInvoiceReference?: string; // FreeAgent "Paid Invoice Reference"
  paidBillReference?: string; // FreeAgent "Paid Bill Reference"
  linkedInvoiceId?: string; // resolved FinancialRecord id (type=invoice)
  linkedBillId?: string; // resolved FinancialRecord id (type=bill)
  /**
   * Resolved FinancialRecord id when the matched record is a reclassified
   * service-charge demand (`type = 'service_charge'`, from Bucket C's
   * auto-reclassify path or a manual ContactDetails reclassify). Populated
   * by the same code paths that populate `linkedInvoiceId` / `linkedBillId`
   * (import Step 6b, `finance/reconcile`) so the bank-tx detail page can
   * resolve to the originating service-charge record and the "cross-ref"
   * verification link can be rendered symmetrically for all three receivable
   * / payable types.
   */
  linkedServiceChargeId?: string;
  chequeNumber?: string;
  receiptReference?: string;
  vatAmount?: number; // pence

  /**
   * Cumulative pence allocated from this transaction across one or more
   * FinancialRecords (reconciliation Req 4, multi-item split). Always
   * `<= |grossValue|`. The transaction is `reconciled` only when
   * `allocatedAmount === |grossValue|` (the whole line is explained).
   */
  allocatedAmount?: number;
  /**
   * Per-item allocations that make up `allocatedAmount` — the audit/display
   * trail for a transaction split across several items. The General Ledger
   * (one Settlement posting per allocation) remains authoritative.
   */
  allocations?: BankTransactionAllocation[];

  attachment?: string; // URL or filename from import
  /**
   * S3 object keys of evidence files attached to this transaction's
   * explanation (reconciliation Req 13), under
   * `reconciliation/{ownerId}/{bankTransactionId}/{filename}`. Uploaded via
   * pre-signed URL; retained even if the transaction is later unmatched.
   */
  attachments?: string[];

  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
}
